// Thin typed API client: JSON fetch wrapper, multipart upload with progress,
// and an SSE helper for live job logs/progress.

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

// ---- direct-to-storage upload resilience ---------------------------------
const PART_MAX_TRIES = 4;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const isAbortError = (e: unknown) => e instanceof DOMException && e.name === "AbortError";
// Transient = worth retrying: a dropped connection (status 0), rate-limit (429),
// a storage 5xx, or a 403 (usually an expired presigned URL → re-signing fixes
// it). A confirmed bad response (e.g. a 2xx with no ETag) is NOT retried.
const _RETRYABLE_UPLOAD_STATUS = new Set([0, 403, 408, 429, 500, 502, 503, 504]);
const isRetryableUpload = (e: unknown) =>
  e instanceof ApiError && _RETRYABLE_UPLOAD_STATUS.has(e.status);

/**
 * Authorization header carrying the current Clerk session JWT, if signed in.
 * Reads the global ClerkJS instance (set by ClerkProvider) so this works
 * outside React and is a clean no-op when auth isn't mounted (local mode) —
 * the backend then falls back to its single-tenant dev user.
 */
export async function authHeaders(): Promise<Record<string, string>> {
  const clerk = (window as unknown as { Clerk?: { session?: { getToken: () => Promise<string | null> } } }).Clerk;
  try {
    if (clerk?.session) {
      const token = await clerk.session.getToken();
      if (token) return { Authorization: `Bearer ${token}` };
    }
  } catch {
    /* not signed in / Clerk not ready — fall through unauthenticated */
  }
  return {};
}

async function parseError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body.detail === "string") return body.detail;
    if (body.detail) return JSON.stringify(body.detail);
    if (body.error) return body.error;
  } catch {
    /* ignore */
  }
  return `HTTP ${res.status}`;
}

export async function api<T = unknown>(
  path: string,
  opts: RequestInit = {}
): Promise<T> {
  const auth = await authHeaders();
  const res = await fetch(path, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...auth,
      ...opts.headers,
    },
    ...opts,
  });
  if (!res.ok) throw new ApiError(await parseError(res), res.status);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// Where the browser keeps answers between visits (see apiKept).
const KEPT_ANSWERS = "mockingbird-answers-v1";

/**
 * A GET whose answer the browser keeps between visits (Cache Storage) and asks
 * for again with its ETag: an unchanged answer comes back as an empty 304 and
 * the kept copy is used. For a large answer a page needs the moment it loads
 * (the project page's flag list behind its Review column): the server can say
 * "unchanged" without reading a file. Without Cache Storage (storage blocked)
 * it's a plain request.
 */
export async function apiKept<T = unknown>(path: string): Promise<T> {
  let store: Cache | undefined;
  let kept: Response | undefined;
  try {
    store = await caches.open(KEPT_ANSWERS);
    kept = await store.match(path);
  } catch {
    /* no Cache Storage here: ask in full */
  }
  const etag = kept?.headers.get("ETag");
  const res = await fetch(path, {
    headers: { Accept: "application/json", ...(await authHeaders()), ...(etag ? { "If-None-Match": etag } : {}) },
    cache: "no-store",
  });
  if (res.status === 304 && kept) return kept.json() as Promise<T>;
  if (!res.ok) throw new ApiError(await parseError(res), res.status);
  if (store && res.headers.get("ETag")) store.put(path, res.clone()).catch(() => { /* full: ask in full next time */ });
  return res.json() as Promise<T>;
}

/**
 * Fetch a binary attachment (with auth, a no-op when signed out) and save it via
 * a temporary object URL. Used by the Sharing Centre's export downloads — works
 * on both the owner (authed) and the public (token) endpoints.
 */
/** Download a LARGE file (an audio take) without buffering it in memory.
 *
 *  `downloadFile` below fetches into a blob first, which is fine for a few-MB
 *  manuscript and hopeless for a several-hundred-MB WAV. Here the API only hands
 *  back a URL — a presigned bucket URL in production — and the browser fetches
 *  it natively, streaming to disk. Nothing transits the app and nothing is held
 *  in RAM. Returns the resolved filename so callers can report progress. */
export async function downloadViaUrl(path: string): Promise<string> {
  const res = await api<{ url: string; filename: string; direct: boolean }>(path);
  const a = document.createElement("a");
  a.href = res.url;
  // Same-origin (local/dev) downloads honour `download`; a cross-origin presigned
  // URL ignores it, which is why the presign carries its own attachment
  // disposition — that, not this attribute, is what names the saved file there.
  a.download = res.filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  return res.filename;
}

export async function downloadFile(path: string, init?: RequestInit): Promise<void> {
  const auth = await authHeaders();
  const res = await fetch(path, { ...init, headers: { ...auth, ...(init?.headers as Record<string, string> | undefined) } });
  if (!res.ok) throw new ApiError(await parseError(res), res.status);
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const name = match ? decodeURIComponent(match[1]) : path.split("/").pop() || "download";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetch a binary resource (with the Clerk auth header, a no-op when signed out)
 * and hand back an object URL for an `<img>`/`<audio>` `src`. A plain `src` to
 * an authed endpoint would 401 — the browser can't attach the bearer token — so
 * anything served through the app rather than a presigned URL comes through here.
 * The caller owns the returned URL and must `URL.revokeObjectURL` it when done.
 */
export async function fetchBlobUrl(path: string): Promise<string> {
  const auth = await authHeaders();
  const res = await fetch(path, { headers: { ...auth } });
  if (!res.ok) throw new ApiError(await parseError(res), res.status);
  return URL.createObjectURL(await res.blob());
}

/** Multipart upload via XHR so we get real upload progress (fetch can't). */
export async function upload<T = unknown>(
  path: string,
  form: FormData,
  onProgress?: (pct: number, loaded: number, total: number) => void
): Promise<T> {
  const auth = await authHeaders();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", path);
    for (const [k, v] of Object.entries(auth)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && onProgress) {
        onProgress(Math.round((ev.loaded / ev.total) * 100), ev.loaded, ev.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(xhr.responseText ? JSON.parse(xhr.responseText) : (undefined as T));
        } catch {
          resolve(undefined as T);
        }
      } else {
        let msg = `HTTP ${xhr.status}`;
        try {
          const b = JSON.parse(xhr.responseText);
          msg = (typeof b.detail === "string" ? b.detail : b.error) || msg;
        } catch {
          /* ignore */
        }
        reject(new ApiError(msg, xhr.status));
      }
    };
    xhr.onerror = () => reject(new ApiError("Network error during upload", 0));
    xhr.send(form);
  });
}

/**
 * Upload one audio file in small sequential chunks to `/audio/chunk`.
 *
 * Chapter takes run 50 MB–1 GB; a single large POST gets dropped by host
 * proxies (Render returns a network error / 413 / 502 well before the file
 * finishes). Slicing into ~8 MB pieces keeps every request tiny and reliable.
 * Chunks are sent in order (the server truncates on chunk 0, appends after,
 * and finalizes on the last). Progress is reported per chunk.
 */
export async function uploadAudioChunked(
  projectId: string,
  file: File,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
  autoProof = true,
  chunkSize = 8 * 1024 * 1024
): Promise<string> {
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
  let saved = file.name;
  for (let i = 0; i < totalChunks; i++) {
    if (signal?.aborted) throw new DOMException("aborted", "AbortError");
    const start = i * chunkSize;
    const blob = file.slice(start, Math.min(start + chunkSize, file.size));
    const auth = await authHeaders();
    const qs = new URLSearchParams({
      filename: file.name,
      chunk_index: String(i),
      total_chunks: String(totalChunks),
      auto_proof: String(autoProof),
    });
    const res = await fetch(`/api/projects/${projectId}/audio/chunk?${qs.toString()}`, {
      method: "POST",
      headers: { ...auth, "Content-Type": "application/octet-stream" },
      body: blob,
      signal,
    });
    if (!res.ok) throw new ApiError(await parseError(res), res.status);
    onProgress?.(Math.round(((i + 1) / totalChunks) * 100));
    if (i === totalChunks - 1) {
      const done = await res.json().catch(() => ({}));
      saved = done.saved || saved;
    }
  }
  return saved;
}

/** PUT one part's bytes straight to a presigned R2 URL (XHR for progress +
 *  to read the ETag header, which the bucket CORS must expose). */
function putPart(url: string, blob: Blob, onLoaded: (loaded: number) => void, signal?: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(new DOMException("aborted", "AbortError")); return; }
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    if (signal) signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.onabort = () => reject(new DOMException("aborted", "AbortError"));
    xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) onLoaded(ev.loaded); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag");
        if (!etag) {
          // No confirmation header came back (most often a misconfigured CORS
          // ExposeHeaders) — not retryable, so surface a coded message.
          reject(new ApiError(
            "Upload couldn't be confirmed (error MB-U03). Please retry; if it keeps happening, contact support and quote this code.",
            xhr.status,
          ));
          return;
        }
        resolve(etag);
      } else {
        reject(new ApiError(
          `Upload couldn't be completed (error MB-U02·${xhr.status}). Please retry; if it keeps happening, contact support and quote this code.`,
          xhr.status,
        ));
      }
    };
    // Status 0 = the request never got a response: a dropped/interrupted
    // connection (by far the most common cause on long uploads) or, rarely, a
    // CORS block. We retry automatically, so this only surfaces once retries are
    // exhausted — keep it customer-friendly and route persistent cases to support.
    xhr.onerror = () =>
      reject(new ApiError(
        "Upload couldn't be completed — the connection dropped before the file finished sending. This is usually temporary; please retry. If it keeps happening, contact support and quote error MB-U01.",
        0,
      ));
    xhr.send(blob);
  });
}

/**
 * Upload one file straight to the bucket via presigned multipart: parallel parts, per-part retry, and an
 * abort of the staged parts if it fails. ``base`` is the endpoint prefix that owns the key and serves
 * ``/sign``, ``/complete`` and ``/abort`` (a project's audio, or a Noise Lab pair side). Resolves with the
 * JSON ``/complete`` returns.
 */
export async function multipartUploadTo<T = unknown>(
  base: string,
  file: File,
  key: string,
  uploadId: string,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
  completeExtra: Record<string, unknown> = {},
  partSize = 16 * 1024 * 1024,
  concurrency = 4
): Promise<T> {
  const total = Math.max(1, Math.ceil(file.size / partSize));
  const loaded = new Array(total).fill(0);
  const parts: { ETag: string; PartNumber: number }[] = new Array(total);
  const report = () =>
    onProgress?.(Math.min(100, Math.round((loaded.reduce((a, b) => a + b, 0) / Math.max(1, file.size)) * 100)));
  let next = 0;
  let failed: unknown = null;

  async function worker() {
    while (next < total && !failed) {
      if (signal?.aborted) { failed = failed || new DOMException("aborted", "AbortError"); return; }
      const i = next++;
      const start = i * partSize;
      const blob = file.slice(start, Math.min(start + partSize, file.size));
      const partNumber = i + 1;
      // Per-part retry: a single dropped part (status 0) or a transient storage
      // hiccup (429/5xx, or a 403 from an expired presign) shouldn't fail the
      // whole file — re-sign + retry with backoff before giving up. Aborts and
      // non-transient errors bail immediately.
      let lastErr: unknown = null;
      for (let attempt = 1; attempt <= PART_MAX_TRIES && !failed; attempt++) {
        if (signal?.aborted) { failed = failed || new DOMException("aborted", "AbortError"); return; }
        try {
          const { url } = await api<{ url: string }>(
            `${base}/sign`,
            { method: "POST", body: JSON.stringify({ key, upload_id: uploadId, part_number: partNumber }), signal }
          );
          const etag = await putPart(url, blob, (l) => { loaded[i] = l; report(); }, signal);
          loaded[i] = blob.size;
          report();
          parts[i] = { ETag: etag, PartNumber: partNumber };
          lastErr = null;
          break;
        } catch (e) {
          if (isAbortError(e)) { failed = failed || e; return; }
          lastErr = e;
          if (attempt < PART_MAX_TRIES && isRetryableUpload(e)) {
            loaded[i] = 0; report();  // reset this part's progress before retrying
            await sleep(400 * 2 ** (attempt - 1) + Math.random() * 250);  // ~0.4s, 0.8s, 1.6s + jitter
            continue;
          }
          break;
        }
      }
      if (lastErr) { failed = failed || lastErr; return; }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, total) }, worker));

  if (failed) {
    try {
      await api(`${base}/abort`, {
        method: "POST",
        body: JSON.stringify({ key, upload_id: uploadId }),
      });
    } catch { /* best-effort */ }
    throw failed;
  }

  const done = await api<T>(
    `${base}/complete`,
    { method: "POST", body: JSON.stringify({ key, upload_id: uploadId, parts, ...completeExtra }) }
  );
  onProgress?.(100);
  return done;
}

/** Upload one project take to R2 via presigned multipart (see multipartUploadTo). */
async function uploadAudioR2Multipart(
  projectId: string,
  file: File,
  key: string,
  uploadId: string,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
  autoProof = true,
  displayName = "",
): Promise<string> {
  const done = await multipartUploadTo<{ filename?: string }>(
    `/api/projects/${projectId}/audio/multipart`, file, key, uploadId, onProgress, signal,
    { auto_proof: autoProof, display_name: displayName },
  );
  return done.filename || key.split("/").pop() || file.name;
}

/**
 * Upload one audio take, negotiating the fastest available path:
 *  - object storage configured → browser-direct parallel multipart to R2;
 *  - otherwise → chunked upload through the app (works everywhere).
 */
export async function uploadAudio(
  projectId: string,
  file: File,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
  autoProof = true
): Promise<string> {
  const init = await api<{
    storage: string; key?: string; upload_id?: string;
    filename?: string; display_name?: string;
  }>(
    `/api/projects/${projectId}/audio/multipart/create`,
    { method: "POST", body: JSON.stringify({ filename: file.name }), signal }
  );
  if (init.storage === "r2" && init.key && init.upload_id) {
    return uploadAudioR2Multipart(
      projectId, file, init.key, init.upload_id, onProgress, signal, autoProof,
      init.display_name || ""
    );
  }
  return uploadAudioChunked(projectId, file, onProgress, signal, autoProof);
}

export interface JobStreamHandlers {
  onMeta?: (data: any) => void;
  onLog?: (line: string, index: number) => void;
  onProgress?: (pct: number, stage: string | null) => void;
  onEnd?: (data: any) => void;
  onError?: () => void;
}

/** Subscribe to a job's SSE stream. Returns a cleanup function. */
export function streamJob(
  jobId: string,
  handlers: JobStreamHandlers,
  fromIndex = 0
): () => void {
  const es = new EventSource(`/api/jobs/${jobId}/events?from=${fromIndex}`);
  const parse = (e: MessageEvent) => {
    try {
      return JSON.parse(e.data);
    } catch {
      return null;
    }
  };
  es.addEventListener("meta", (e) => handlers.onMeta?.(parse(e as MessageEvent)));
  es.addEventListener("log", (e) => {
    const d = parse(e as MessageEvent);
    if (d) handlers.onLog?.(d.line, d.index);
  });
  es.addEventListener("progress", (e) => {
    const d = parse(e as MessageEvent);
    if (d) handlers.onProgress?.(d.progress_pct, d.stage);
  });
  es.addEventListener("end", (e) => {
    handlers.onEnd?.(parse(e as MessageEvent));
    es.close();
  });
  es.onerror = () => handlers.onError?.();
  return () => es.close();
}
